import { useEffect, useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
// d3-transition patches `selection.interrupt` onto d3-selection's prototype,
// which d3-zoom (used internally by react-simple-maps' ZoomableGroup) relies
// on. Without this side-effect import the map throws
// "selection.interrupt is not a function" on mount.
import "d3-transition";
import { geoPath, geoCentroid, geoNaturalEarth1 } from "d3-geo";
import { Delaunay } from "d3-delaunay";
import { Search, Crosshair, Plus, Minus, RotateCcw, X } from "lucide-react";
import { api } from "@/lib/api";

const TOPO_URL = "/data/world-countries-50m.json";

// A neutral swatch used for the ~104 non-catalogue territories rendered under
// the 137 playable ones. Kept intentionally low-contrast so the eye is drawn
// to owned/active nations.
const OFFROSTER_FILL = "#1a2331";
const OFFROSTER_STROKE = "#0a1220";

const OWNER_TINT = {
  neutral: null,        // use per-country palette colour
  contested: "#c8484a", // muted crimson
  ally:      "#4c8f6a",
  hostile:   "#a3423f",
};

function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Interactive world map — Phase 1 (137 nations).
 *
 * Data flow:
 *  - The world-atlas 50m TopoJSON ships all ~241 sovereign & disputed
 *    territories. We fetch the 137-entry catalogue from the backend and
 *    build an index of (normalised topo-name → country entry). Any geometry
 *    NOT in that index paints as a subdued "off-roster" swatch so the
 *    playable nations stay visually prominent.
 *  - `ZoomableGroup` (from react-simple-maps) handles pan+wheel-zoom on an
 *    SVG canvas. We drive the `center`/`zoom` state from search + fit-view.
 */
export function WorldMap({
  onSelectCountry, selectedIso, markers = [], onCountriesLoaded,
  selectedRegionId, onSelectRegion,
  myHolderIso,
  mapOwnership = {}, setMapOwnership,
  homeIso,
}) {
  const [countries, setCountries] = useState([]);          // 174 catalogue
  const [byTopo, setByTopo] = useState(new Map());          // name → entry
  const [byIso, setByIso] = useState(new Map());            // iso → entry
  const [hover, setHover] = useState(null);                 // { entry, x, y }
  const [hoverRegion, setHoverRegion] = useState(null);     // { region, country, x, y }
  const [pos, setPos] = useState({ coordinates: [10, 20], zoom: 1 });
  const [query, setQuery] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const centroidsRef = useRef(new Map()); // iso → [lon, lat]
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get("/api/game/map/countries").then(({ data }) => {      if (!mounted) return;
      const list = data.countries || [];
      setCountries(list);
      const idx = new Map();
      const isoIdx = new Map();
      const initialOwnership = {};
      for (const c of list) {
        for (const n of c.topo_names || []) idx.set(n, c);
        idx.set(c.name, c);
        isoIdx.set(c.iso_a3, c);
        for (const r of c.regions || []) {
          if (r.owner) initialOwnership[r.id] = r.owner;
        }
      }
      setByTopo(idx);
      setByIso(isoIdx);
      if (setMapOwnership && Object.keys(initialOwnership).length) {
        setMapOwnership(initialOwnership);
      }
      onCountriesLoaded?.(list);
    }).catch(() => { /* silent */ });
    return () => { mounted = false; };
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return countries.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.legacy_name || "").toLowerCase().includes(q)
      || c.iso_a3.toLowerCase() === q,
    ).slice(0, 8);
  }, [countries, query]);

  function focusCountry(entry) {
    const c = centroidsRef.current.get(entry.iso_a3);
    if (c) {
      setPos({ coordinates: c, zoom: Math.max(4, pos.zoom) });
    } else {
      setPos((p) => ({ coordinates: p.coordinates, zoom: Math.max(3, p.zoom) }));
    }
    setQuery(entry.name);
    setShowSuggest(false);
    onSelectCountry?.(entry);
  }

  function resetView() { setPos({ coordinates: [10, 20], zoom: 1 }); }
  function zoomIn()   { setPos((p) => ({ ...p, zoom: Math.min(8, p.zoom * 1.6) })); }
  function zoomOut()  { setPos((p) => ({ ...p, zoom: Math.max(1, p.zoom / 1.6) })); }

  // Recentre when the parent programmatically selects a country (e.g. the
  // home-country auto-focus on the Map page). Skipped when centroid cache
  // isn't populated yet — the /countries fetch primes it on first paint.
  useEffect(() => {
    if (!selectedIso) return;
    const c = centroidsRef.current.get(selectedIso);
    if (c) setPos((p) => ({ coordinates: c, zoom: Math.max(3, p.zoom) }));
  }, [selectedIso]);

  const handleGeoClick = (geo, entry, projectedCentroid) => {
    if (!entry) return;
    setPos({ coordinates: projectedCentroid, zoom: Math.max(3, pos.zoom) });
    onSelectCountry?.(entry);
  };

  return (
    <div ref={wrapRef} className="relative w-full h-[62vh] min-h-[420px] rounded-md overflow-hidden border border-stone-800 bg-[#050914]" data-testid="world-map">
      {/* Radial vignette to give the canvas depth */}
      <div className="absolute inset-0 pointer-events-none z-10"
        style={{ background: "radial-gradient(ellipse at 50% 55%, transparent 45%, rgba(0,0,0,0.55) 100%)" }} />

      {/* Search + zoom controls */}
      <div className="absolute top-3 left-3 z-20 flex items-start gap-2">
        <div className="relative">
          <div className="flex items-center gap-2 bg-[#0b1220]/85 border border-stone-700 rounded px-2.5 py-1.5 backdrop-blur-sm min-w-[280px]">
            <Search size={13} className="text-stone-500 shrink-0" />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              placeholder="Search players, countries, regions"
              className="bg-transparent text-sm text-stone-100 placeholder:text-stone-500 outline-none flex-1 font-body"
              data-testid="map-search-input"
            />
            {query && (
              <button onClick={() => { setQuery(""); setShowSuggest(false); }} className="text-stone-500 hover:text-stone-300">
                <X size={12} />
              </button>
            )}
          </div>
          {showSuggest && suggestions.length > 0 && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-[#0b1220]/95 border border-stone-700 rounded overflow-hidden backdrop-blur-md">
              {suggestions.map((c) => (
                <button
                  key={c.iso_a3}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => focusCountry(c)}
                  className="w-full px-3 py-2 text-left hover:bg-stone-800/60 flex items-center gap-2 text-sm border-b border-stone-800/60 last:border-0"
                  data-testid={`map-suggest-${c.iso_a3}`}
                >
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: c.color }} />
                  <span className="text-stone-100 flex-1">{c.name}</span>
                  <span className="text-[10px] font-numeric text-stone-500">{c.iso_a3}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="absolute top-3 right-3 z-20 flex flex-col gap-1">
        <button onClick={zoomIn}   className="w-8 h-8 grid place-items-center bg-[#0b1220]/85 border border-stone-700 rounded text-stone-300 hover:text-white hover:border-stone-500" data-testid="map-zoom-in"><Plus size={14} /></button>
        <button onClick={zoomOut}  className="w-8 h-8 grid place-items-center bg-[#0b1220]/85 border border-stone-700 rounded text-stone-300 hover:text-white hover:border-stone-500" data-testid="map-zoom-out"><Minus size={14} /></button>
        <button onClick={resetView} className="w-8 h-8 grid place-items-center bg-[#0b1220]/85 border border-stone-700 rounded text-stone-300 hover:text-white hover:border-stone-500" title="Reset view" data-testid="map-reset"><RotateCcw size={13} /></button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-20 bg-[#0b1220]/85 border border-stone-700 rounded px-3 py-2 text-[10px] font-numeric uppercase tracking-wider text-stone-400 flex items-center gap-3 backdrop-blur-sm">
        <span className="text-stone-500">Regions</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#d97e6c" }} />foreign</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm ring-1 ring-amber-300" style={{ background: "#d97e6c" }} />yours</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: OFFROSTER_FILL, border: "1px solid #1f2a3a" }} />off-roster</span>
      </div>

      {/* Map */}
      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{ scale: 155 }}
        width={980}
        height={520}
        style={{ width: "100%", height: "100%" }}
      >
        <defs>
          <radialGradient id="mapGlow" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#0d1a2c" />
            <stop offset="100%" stopColor="#050914" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="980" height="520" fill="url(#mapGlow)" />
        <ZoomableGroup
          center={pos.coordinates}
          zoom={pos.zoom}
          onMoveEnd={(p) => setPos(p)}
          maxZoom={8}
          minZoom={1}
        >
          <Geographies geography={TOPO_URL}>
            {({ geographies, projection }) => {
              // Populate the centroid lookup once per topology mount so the
              // search / recentre logic can jump straight to a country's
              // geographical middle rather than the last-known pan position.
              if (geographies.length && centroidsRef.current.size === 0) {
                for (const g of geographies) {
                  const e = byTopo.get(g.properties.name);
                  if (e) centroidsRef.current.set(e.iso_a3, geoCentroid(g));
                }
              }
              return geographies.map((geo) => {
              const topoName = geo.properties.name;
              const entry = byTopo.get(topoName);
              const isPlayable = !!entry;
              const isSelected = entry && selectedIso === entry.iso_a3;

              let fill = OFFROSTER_FILL;
              let stroke = OFFROSTER_STROKE;
              const isHome = entry && homeIso === entry.iso_a3;
              if (entry) {
                // If the country has a dominant owner (>= 50% region control
                // by a single player), retint the base fill in that faction
                // colour so the world map reads at a glance.
                let baseColor = entry.color;
                let ownerCount = 0;
                for (const r of entry.regions || []) {
                  if (mapOwnership[r.id]) ownerCount++;
                }
                if (ownerCount) {
                  const holders = {};
                  for (const r of entry.regions || []) {
                    const o = mapOwnership[r.id];
                    if (o) holders[o.id] = (holders[o.id] || 0) + 1;
                  }
                  const [topId, topN] = Object.entries(holders)
                    .sort((a, b) => b[1] - a[1])[0];
                  if (topN / entry.regions.length >= 0.5) {
                    const rec = Object.values(mapOwnership).find((o) => o.id === topId);
                    if (rec) baseColor = rec.faction_color;
                  }
                }
                fill = baseColor;
                stroke = hexToRgba(baseColor, 0.35);
              }
              if (isHome) stroke = "#ffe7a3";
              if (isSelected) stroke = "#ffe7a3";

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  data-testid={entry ? `country-${entry.iso_a3}` : undefined}
                  onMouseEnter={(e) => {
                    if (!wrapRef.current) return;
                    const rect = wrapRef.current.getBoundingClientRect();
                    setHover({
                      entry,
                      topoName,
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                    });
                  }}
                  onMouseMove={(e) => {
                    if (!wrapRef.current || !hover) return;
                    const rect = wrapRef.current.getBoundingClientRect();
                    setHover((h) => (h ? { ...h, x: e.clientX - rect.left, y: e.clientY - rect.top } : h));
                  }}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => {
                    if (!entry) return;
                    // Use d3-geo centroid — more accurate than ring-midpoint,
                    // handles MultiPolygon (islands, exclaves) correctly.
                    try {
                      const c = geoCentroid(geo) || pos.coordinates;
                      handleGeoClick(geo, entry, c);
                    } catch { onSelectCountry?.(entry); }
                  }}
                  style={{
                    default: {
                      fill,
                      stroke,
                      strokeWidth: isSelected ? 1.2 : isHome ? 0.9 : 0.4,
                      outline: "none",
                      cursor: isPlayable ? "pointer" : "default",
                      transition: "fill 200ms, stroke 200ms",
                    },
                    hover: {
                      fill: isPlayable ? hexToRgba(fill, 0.95) : fill,
                      stroke: isPlayable ? "#ffe7a3" : stroke,
                      strokeWidth: isPlayable ? 0.9 : 0.4,
                      outline: "none",
                      filter: isPlayable ? "brightness(1.15)" : undefined,
                    },
                    pressed: { fill, outline: "none" },
                  }}
                />
              );
            });
            }}
          </Geographies>

          {/* Phase-2 region subunit overlay — only for the selected country. */}
          {selectedIso && (
            <Geographies geography={TOPO_URL}>
              {({ geographies, projection }) => {
                const selectedGeo = geographies.find((g) => {
                  const entry = byTopo.get(g.properties.name);
                  return entry && entry.iso_a3 === selectedIso;
                });
                if (!selectedGeo) return null;
                const entry = byIso.get(selectedIso);
                if (!entry?.regions?.length) return null;
                return (
                  <RegionOverlay
                    geo={selectedGeo}
                    entry={entry}
                    projection={projection}
                    wrapRef={wrapRef}
                    hoverRegion={hoverRegion}
                    setHoverRegion={setHoverRegion}
                    selectedRegionId={selectedRegionId}
                    onSelectRegion={onSelectRegion}
                    mapOwnership={mapOwnership}
                    myHolderIso={myHolderIso}
                  />
                );
              }}
            </Geographies>
          )}

          {markers.map((m, i) => {
            const isSelf = m.kind === "self";
            return (
              <Marker key={m.id || i} coordinates={[m.lon, m.lat]}>
                <g style={{ pointerEvents: "auto", cursor: "pointer" }}>
                  <title>{m.title || m.name}</title>
                  {isSelf ? (
                    <>
                      {/* Subtle pulse — small enough not to blanket a country */}
                      <circle r={3} fill="#f5c26b" opacity={0.35}>
                        <animate attributeName="r" values="2;5;2" dur="2.4s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.55;0.05;0.55" dur="2.4s" repeatCount="indefinite" />
                      </circle>
                      <circle r={1.9} fill="#f5c26b" stroke="#0a0f1c" strokeWidth={0.5} />
                      <polygon points="0,-4.5 1.3,-1.7 -1.3,-1.7" fill="#f5c26b" stroke="#0a0f1c" strokeWidth={0.4} />
                    </>
                  ) : (
                    <>
                      <circle r={2.6} fill="#0a0f1c" stroke="#8ac54c" strokeWidth={0.6} />
                      <circle r={1} fill="#8ac54c" />
                    </>
                  )}
                </g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {hover && !hoverRegion && (
        <div
          className="pointer-events-none absolute z-30 bg-[#0b1220]/95 border border-stone-700 rounded px-2.5 py-1.5 shadow-lg backdrop-blur-sm"
          style={{ left: hover.x + 14, top: hover.y + 14, maxWidth: 240 }}
        >
          {hover.entry ? (
            <>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: hover.entry.color }} />
                <div className="text-sm text-stone-100 font-display">{hover.entry.name}</div>
                <span className="text-[10px] font-numeric text-stone-500 ml-auto">{hover.entry.iso_a3}</span>
              </div>
              <div className="text-[10px] font-numeric uppercase tracking-wider text-stone-500 mt-0.5">
                {hover.entry.owner ? `Owner · ${hover.entry.owner}` : `Status · ${hover.entry.faction}`}
                {hover.entry.region_count ? ` · ${hover.entry.region_count} regions` : ""}
              </div>
              {homeIso === hover.entry.iso_a3 && (
                <div className="text-[10px] font-numeric uppercase tracking-wider text-amber-400/90 mt-0.5">
                  · your homeland ·
                </div>
              )}
              {hover.entry.legacy_name && (
                <div className="text-[10px] font-body italic text-stone-500 mt-0.5">
                  legacy · {hover.entry.legacy_name}
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] font-body text-stone-400">{hover.topoName} <span className="text-stone-600">— off-roster</span></div>
          )}
        </div>
      )}

      {hoverRegion && (
        <div
          className="pointer-events-none absolute z-30 bg-[#0b1220]/95 border border-amber-600/60 rounded px-2.5 py-1.5 shadow-lg backdrop-blur-sm"
          style={{ left: hoverRegion.x + 14, top: hoverRegion.y + 14, maxWidth: 260 }}
        >
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: hoverRegion.owner?.faction_color || hoverRegion.region.color }} />
            <div className="text-sm text-stone-100 font-display">{hoverRegion.region.name}</div>
          </div>
          <div className="text-[10px] font-numeric uppercase tracking-wider text-stone-500 mt-0.5">
            {hoverRegion.country.name} · weight {hoverRegion.region.weight}
          </div>
          {hoverRegion.owner ? (
            <div className="text-[10px] font-body italic text-amber-500/90 mt-0.5">
              held by {hoverRegion.owner.holder_name || hoverRegion.owner.name}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// --- helpers -------------------------------------------------------------

/** Cheap 32-bit string hash — used to seed region centroids so the same
 *  region always tessellates in the same spot. */
function hash32(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Voronoi-based region overlay for the currently-selected country.
 *
 * Seed placement (in projected SVG coords) picks the *first* available:
 *  1. `region.seed = [lon, lat]` — real geographic anchor (flagship regions);
 *     projected through the same d3-geo projection so the seed lands over
 *     the correct real-world location (e.g. Deccan sits in central India).
 *  2. Compass name — "Northern X" / "Eastern X" / "Southern X" / "Western X"
 *     / "Central X" — placed at the corresponding side/centre of the
 *     country's bounding box in projected SVG coords.
 *  3. Deterministic name hash inside the bbox (last-resort fallback so a
 *     brand-new region name still gets a stable position).
 *
 * All seeds are then handed to d3-delaunay and each cell is clipped to the
 * country's SVG silhouette via a per-country `<clipPath>`.
 */
function RegionOverlay({
  geo, entry, projection, wrapRef,
  hoverRegion, setHoverRegion, selectedRegionId, onSelectRegion,
  mapOwnership = {}, myHolderIso,
}) {
  const pathGen = geoPath(projection);
  const dCountry = pathGen(geo);
  const bounds = pathGen.bounds(geo);       // [[x0,y0],[x1,y1]] in SVG coords
  if (!dCountry || !bounds) return null;
  const [[x0, y0], [x1, y1]] = bounds;
  const w = Math.max(1, x1 - x0);
  const h = Math.max(1, y1 - y0);
  const regions = entry.regions;

  // Compass positions inside the country's projected bbox. Inset slightly
  // so Voronoi cells don't get pinched at the edges.
  const compass = {
    n:  [x0 + w * 0.50, y0 + h * 0.25],
    e:  [x0 + w * 0.75, y0 + h * 0.50],
    s:  [x0 + w * 0.50, y0 + h * 0.75],
    w:  [x0 + w * 0.25, y0 + h * 0.50],
    c:  [x0 + w * 0.50, y0 + h * 0.50],
  };

  function seedFor(region) {
    // 1. Real geographic anchor
    if (region.seed && region.seed[0] != null && region.seed[1] != null) {
      const p = projection(region.seed);
      if (p && Number.isFinite(p[0]) && Number.isFinite(p[1])) return p;
    }
    // 2. Compass by name
    const n = region.name.toLowerCase();
    if (n.startsWith("northern") || n.startsWith("north ")) return compass.n;
    if (n.startsWith("southern") || n.startsWith("south ")) return compass.s;
    if (n.startsWith("eastern")  || n.startsWith("east "))  return compass.e;
    if (n.startsWith("western")  || n.startsWith("west "))  return compass.w;
    if (n.startsWith("central")) return compass.c;
    // 3. Deterministic hash fallback
    const hA = hash32(`${entry.iso_a3}:${region.id}:x`);
    const hB = hash32(`${entry.iso_a3}:${region.id}:y`);
    const fx = 0.15 + (hA % 10000) / 10000 * 0.7;
    const fy = 0.15 + (hB % 10000) / 10000 * 0.7;
    return [x0 + fx * w, y0 + fy * h];
  }

  const seeds = regions.map(seedFor);

  // Degenerate single-region country (microstate) — just paint one wash.
  if (seeds.length === 1) {
    const r = regions[0];
    const isSel = selectedRegionId === r.id;
    return (
      <g>
        <defs>
          <clipPath id={`region-clip-${entry.iso_a3}`}><path d={dCountry} /></clipPath>
        </defs>
        <rect
          x={x0} y={y0} width={w} height={h}
          fill={r.color}
          fillOpacity={isSel ? 0.85 : 0.55}
          stroke="none"
          clipPath={`url(#region-clip-${entry.iso_a3})`}
          style={{ cursor: "pointer" }}
          onClick={() => onSelectRegion?.(r, entry)}
        />
      </g>
    );
  }

  const voronoi = Delaunay.from(seeds).voronoi([x0 - 4, y0 - 4, x1 + 4, y1 + 4]);

  return (
    <g data-testid={`region-overlay-${entry.iso_a3}`}>
      <defs>
        <clipPath id={`region-clip-${entry.iso_a3}`}>
          <path d={dCountry} />
        </clipPath>
      </defs>
      {regions.map((r, i) => {
        const d = voronoi.renderCell(i);
        if (!d) return null;
        const isSel  = selectedRegionId === r.id;
        const isHov  = hoverRegion?.region?.id === r.id;
        const owner  = mapOwnership[r.id];
        const isMine = owner && myHolderIso && owner.holder_iso === myHolderIso;
        const fill   = owner ? owner.faction_color : r.color;
        const strokeCol =
          isSel   ? "#ffe7a3"
          : isMine ? "#ffe7a3"
          : owner  ? hexToRgba(owner.faction_color, 0.8)
          : "rgba(0,0,0,0.35)";
        return (
          <path
            key={r.id}
            d={d}
            data-testid={`region-${r.id}`}
            clipPath={`url(#region-clip-${entry.iso_a3})`}
            fill={fill}
            fillOpacity={
              isSel ? 0.9
              : owner ? (isHov ? 0.82 : 0.72)
              : (isHov ? 0.72 : 0.5)
            }
            stroke={strokeCol}
            strokeWidth={isSel ? 0.7 : isMine ? 0.55 : 0.3}
            style={{ cursor: "pointer", transition: "fill-opacity 150ms, fill 250ms" }}
            onMouseEnter={(e) => {
              if (!wrapRef.current) return;
              const rect = wrapRef.current.getBoundingClientRect();
              setHoverRegion({
                region: r, country: entry, owner,
                x: e.clientX - rect.left, y: e.clientY - rect.top,
              });
            }}
            onMouseMove={(e) => {
              if (!wrapRef.current) return;
              const rect = wrapRef.current.getBoundingClientRect();
              setHoverRegion((hr) => (hr ? { ...hr, x: e.clientX - rect.left, y: e.clientY - rect.top } : hr));
            }}
            onMouseLeave={() => setHoverRegion(null)}
            onClick={(e) => { e.stopPropagation(); onSelectRegion?.(r, entry); }}
          />
        );
      })}
    </g>
  );
}
